import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { NpdCreateModal } from "./NpdCreateModal";
import { NpdDetailModal } from "./NpdDetailModal";
import { useToast } from "../../components/Toast";

const RESULT_STYLE = {
  PASS:        { background: "#dcfce7", color: "#15803d" },
  FAIL:        { background: "#fee2e2", color: "#dc2626" },
  IN_PROGRESS: { background: "#fef9c3", color: "#92400e" },
};

function ResultBadge({ value }) {
  const s = RESULT_STYLE[value] ?? { background: "#f1f5f9", color: "#475569" };
  const label = value === "IN_PROGRESS" ? "In Progress" : value;
  return (
    <span style={{ ...s, borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
      {label}
    </span>
  );
}

export function PageRdNpd({ myPerms = [], rms = [], sfgs = [] }) {
  const showToast = useToast();
  const canEdit   = myPerms.includes("QC_EDIT") || myPerms.includes("RND_EDIT");
  const canDelete = myPerms.includes("USER_MANAGE");

  const [trials,  setTrials]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState("");
  const [filterResult, setFilterResult] = useState("");
  const [creating,    setCreating]    = useState(false);
  const [selected,    setSelected]    = useState(null);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.fetchNpdTrials();
      setTrials(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return trials.filter(t => {
      if (filterResult && t.result !== filterResult) return false;
      if (q && !t.trialNo?.toLowerCase().includes(q) &&
               !t.prodCode?.toLowerCase().includes(q) &&
               !t.remarks?.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [trials, search, filterResult]);

  async function handleCreate(body) {
    const created = await api.createNpdTrial(body);
    setTrials(list => [created, ...list]);
    setCreating(false);
    showToast("NPD trial created");
  }

  async function handleUpdate(id, body) {
    const updated = await api.updateNpdTrial(id, body);
    setTrials(list => list.map(t => t.id === id ? updated : t));
    if (selected?.id === id) setSelected(updated);
    showToast("Trial updated");
    return updated;
  }

  async function handleDelete(id) {
    await api.deleteNpdTrial(id);
    setTrials(list => list.filter(t => t.id !== id));
    setSelected(null);
    showToast("Trial deleted", "info");
  }

  async function openDetail(trial) {
    try {
      const detail = await api.fetchNpdDetail(trial.id);
      setSelected(detail);
    } catch {
      setSelected(trial);
    }
  }

  const inProgress = trials.filter(t => t.result === "IN_PROGRESS").length;
  const passed     = trials.filter(t => t.result === "PASS").length;
  const failed     = trials.filter(t => t.result === "FAIL").length;

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">New Product Development</div>
          <div className="page-subtitle">Trial records & formulation testing</div>
        </div>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            + New Trial
          </button>
        )}
      </div>

      {/* Summary chips */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <span style={{ background: "#fef9c3", color: "#92400e", borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 600 }}>
          {inProgress} In Progress
        </span>
        <span style={{ background: "#dcfce7", color: "#15803d", borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 600 }}>
          {passed} Passed
        </span>
        <span style={{ background: "#fee2e2", color: "#dc2626", borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 600 }}>
          {failed} Failed
        </span>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <input className="form-input" style={{ maxWidth: 260, margin: 0 }}
          placeholder="Search trial no, product code…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <select className="form-select" style={{ maxWidth: 180, margin: 0 }}
          value={filterResult} onChange={e => setFilterResult(e.target.value)}>
          <option value="">All Results</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="PASS">Pass</option>
          <option value="FAIL">Fail</option>
        </select>
        {(search || filterResult) && (
          <button className="btn" onClick={() => { setSearch(""); setFilterResult(""); }}>
            Clear
          </button>
        )}
      </div>

      {loading && <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Loading…</div>}
      {error   && <div className="alert-warn">{error} <button className="btn" onClick={load}>Retry</button></div>}

      {!loading && !error && (
        filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
            {trials.length === 0 ? "No NPD trials yet. Click \"+ New Trial\" to get started." : "No trials match the current filters."}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={th}>Trial No</th>
                  <th style={th}>Product Code</th>
                  <th style={th}>Date</th>
                  <th style={th}>Sample Qty</th>
                  <th style={th}>Result</th>
                  <th style={th}>Remarks</th>
                  <th style={th}>Created By</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.id}
                    style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}
                    className="table-row-hover"
                    onClick={() => openDetail(t)}>
                    <td style={{ ...td, fontWeight: 600, color: "#1a3a6b" }}>{t.trialNo}</td>
                    <td style={td}>{t.prodCode}</td>
                    <td style={td}>{t.date ?? "—"}</td>
                    <td style={td}>{t.sampleBatchQty ?? "—"}</td>
                    <td style={td}><ResultBadge value={t.result} /></td>
                    <td style={{ ...td, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.remarks ?? "—"}
                    </td>
                    <td style={{ ...td, color: "#64748b" }}>{t.createdBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Create modal */}
      {creating && (
        <div className="overlay">
          <NpdCreateModal onSave={handleCreate} onCancel={() => setCreating(false)} rms={rms} sfgs={sfgs} />
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="overlay">
          <NpdDetailModal
            trial={selected}
            canEdit={canEdit}
            canDelete={canDelete}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onClose={() => setSelected(null)}
            rms={rms}
            sfgs={sfgs}
          />
        </div>
      )}
    </div>
  );
}

const th = { textAlign: "left", padding: "9px 10px", fontWeight: 600, fontSize: 12 };
const td = { padding: "8px 10px" };
